/**
 * `/invite/:token`: someone opened an invitation link. Shows who invited them to what, asks them
 * to sign in if they have not (the token survives the round trip as the sign-in's way back), then
 * joins and makes that team the active organisation.
 */

import type { CloudRole } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';
import { Button } from '@heroui/react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError, problemText, routes } from '../../api/client.ts';
import { useAuth } from '../../auth/AuthProvider.tsx';
import { cloudSession } from '../../auth/cloudSession.ts';
import { AuthFrame, LinkButton, SignInPanel } from '../../components/cloud/SignIn.tsx';
import { ErrorNote } from '../../components/ui.tsx';
import { useLoad } from '../../hooks.ts';

const AS_ROLE: Record<CloudRole, string> = { owner: 'an owner', admin: 'an admin', member: 'a member', viewer: 'a viewer' };
const day = (iso: string) => dateLong(iso, { year: false });

export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { signedOut, cloud } = useAuth();
  const preview = useLoad(() => routes.invitation(token), [token]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; mismatch?: boolean }>();

  if (preview.loading && !preview.data) return <div className="flex h-full items-center justify-center text-sm text-muted">Opening the invitation…</div>;

  const p = preview.data;
  if (!p) {
    return (
      <AuthFrame title="This invitation has ended" blurb="It was withdrawn, already used, or is more than a week old. Ask the person who sent it for a new one.">
        <Button variant="secondary" fullWidth onPress={() => navigate('/', { replace: true })}>
          {signedOut ? 'Sign in to Mojo Up AI Cloud' : 'Go to Mojo Up AI Cloud'}
        </Button>
      </AuthFrame>
    );
  }

  const title = `${p.invitedBy ?? 'Someone'} invited you to ${p.organisation}`;
  const blurb = `Join as ${AS_ROLE[p.role]}. The invitation is for ${p.email} and works until ${day(p.expiresAt)}.`;

  if (signedOut || !cloud) {
    return (
      <AuthFrame title={title} blurb={blurb} footer={`Sign in with any account that uses ${p.email}.`}>
        <SignInPanel returnTo={`/invite/${encodeURIComponent(token)}`} />
      </AuthFrame>
    );
  }

  const accept = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const org = await routes.acceptInvitation(token);
      await cloud.reload();
      cloud.switchTo(org.id);
      navigate('/team', { replace: true });
    } catch (e) {
      setError({ text: problemText(e), mismatch: e instanceof ApiError && e.code === 'email_mismatch' });
      setBusy(false);
    }
  };

  return (
    <AuthFrame title={title} blurb={blurb}>
      <div className="grid gap-4">
        <p className="text-center text-sm text-muted">
          Signed in as <span className="font-medium text-foreground">{cloud.me.email ?? cloud.me.name}</span>
        </p>
        <ErrorNote error={error?.text} />
        {error?.mismatch ? (
          <Button variant="primary" fullWidth onPress={() => void cloudSession.signOut()}>
            Sign in with another account
          </Button>
        ) : (
          <Button variant="primary" fullWidth isPending={busy} onPress={() => void accept()}>
            Join {p.organisation}
          </Button>
        )}
        <div className="text-center text-sm">
          <LinkButton onPress={() => navigate('/', { replace: true })}>Not now</LinkButton>
        </div>
      </div>
    </AuthFrame>
  );
}
